import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { FileUploader } from 'react-drag-drop-files';

import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { MAX_FILE_SIZE_IN_MB } from '~utils/csv/csvProcessor';

interface CSVFileDropzoneProps {
  disabled?: boolean;
  fileSelectionId: number;
  onClear: () => void;
  onSelect: (file: File) => void;
  selectedFileName?: string;
}

export function CSVFileDropzone({
  disabled,
  fileSelectionId,
  onClear,
  onSelect,
  selectedFileName,
}: CSVFileDropzoneProps) {
  const { toasterStore } = useStores();

  const showError = (message: string) =>
    toasterStore.push(alert(message, AlertTypes.error));

  const handleChange = (file: File) => {
    // FileUploader's disabled prop blocks the picker but still accepts a drop.
    if (disabled) {
      return;
    }

    onSelect(file);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <FileUploader
        key={fileSelectionId}
        disabled={disabled}
        dropMessageStyle={{ opacity: 0 }}
        handleChange={handleChange}
        maxSize={MAX_FILE_SIZE_IN_MB}
        name="csv-file-uploader"
        onSizeError={() =>
          showError(t('csv_import_export.file_too_large', { max: MAX_FILE_SIZE_IN_MB }))
        }
        onTypeError={() => showError(t('csv_import_export.file_type_invalid'))}
        types={['CSV']}
      >
        <Box
          data-test-id="csv-dropzone"
          sx={{
            border: 1,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 1,
            py: 3,
            px: 2,
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Typography variant="body2">
            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
              {t('csv_import_export.dropzone.prompt')}
            </Typography>{' '}
            {t('csv_import_export.dropzone.or')}{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{ textDecoration: 'underline' }}
            >
              {t('csv_import_export.dropzone.browse')}
            </Typography>
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('csv_import_export.dropzone.hint', { max: MAX_FILE_SIZE_IN_MB })}
          </Typography>
        </Box>
      </FileUploader>

      {selectedFileName && (
        <Chip
          label={selectedFileName}
          onDelete={onClear}
          size="small"
          sx={{ mt: 1 }}
          variant="outlined"
        />
      )}
    </Box>
  );
}
